import React, { useCallback, useEffect, useRef, useState } from "react";
import { ApolloError, gql, useMutation, useQuery } from "@apollo/client";
import { Box, Flex } from "src/ccl/layout";
import { Modal, Text } from "src/ccl/document";
import { Checkbox } from "src/ccl/data-entry";
import { Button } from "src/ccl/navigation";
import { useStoreState } from "src/hooks";
import { TermsStatus } from "src/graphql/types";
import { SettleTerms, TERMS_CONFIRM_EVENT } from "src/utils/terms";

export const TERMS_STATUS = gql`
  query TermsStatus {
    termsStatus {
      version
      documentSha256
      termsUrl
      privacyUrl
      effectiveAt
      acceptanceRequired
      acceptedAt
    }
  }
`;

export const ACCEPT_TERMS = gql`
  mutation AcceptTerms(
    $version: String!
    $documentSha256: String!
    $accepted: Boolean!
  ) {
    acceptTerms(
      version: $version
      documentSha256: $documentSha256
      accepted: $accepted
    ) {
      acceptedAt
    }
  }
`;

export const TermsAcceptanceNotice = ({
  showBanner = false,
}: {
  showBanner?: boolean;
}) => {
  const userId = useStoreState((state) => state.currentUser.me?.id);
  const loggedIn = useStoreState((state) => !!state.currentUser.accessToken);
  // Remount on account switches, so another account never inherits a checkbox
  // or acceptance result. There is no browser-local acceptance flag.
  return loggedIn && userId ? (
    <TermsAcceptanceReview key={userId} showBanner={showBanner} />
  ) : null;
};

export const TermsAcceptanceReview = ({
  showBanner = false,
}: {
  showBanner?: boolean;
}) => {
  const { data, error, refetch } = useQuery<{
    termsStatus: TermsStatus | null;
  }>(TERMS_STATUS, {
    fetchPolicy: "no-cache",
  });
  const [accept, { loading: saving }] = useMutation(ACCEPT_TERMS);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);
  const terms = data?.termsStatus;
  const termsRef = useRef(terms);
  termsRef.current = terms;
  const pending = useRef<SettleTerms | null>(null);

  const settle = useCallback((accepted: boolean) => {
    pending.current?.(accepted);
    pending.current = null;
  }, []);

  useEffect(() => {
    setChecked(false);
    setSaved(false);
  }, [terms?.version, terms?.documentSha256]);

  useEffect(() => {
    const refresh = () => {
      void refetch().catch(() => undefined);
    };
    const required = () => {
      setOpen(true);
      setChecked(false);
      refresh();
    };
    const confirm = (event: Event) => {
      if (!termsRef.current?.acceptanceRequired) return;
      event.preventDefault();
      settle(false);
      pending.current = (event as CustomEvent<SettleTerms>).detail;
      setOpen(true);
      setChecked(false);
      setSaveError("");
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("contact:terms-required", required);
    window.addEventListener(TERMS_CONFIRM_EVENT, confirm);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("contact:terms-required", required);
      window.removeEventListener(TERMS_CONFIRM_EVENT, confirm);
      settle(false);
    };
  }, [refetch, settle]);

  const close = () => {
    settle(false);
    setOpen(false);
    setChecked(false);
    setSaveError("");
  };

  const submit = async () => {
    if (!checked || !terms || saving) return;
    setSaveError("");
    try {
      await accept({
        variables: {
          version: terms.version,
          documentSha256: terms.documentSha256,
          accepted: true,
        },
      });
      await refetch();
      setSaved(true);
      settle(true);
      close();
    } catch (err) {
      if (
        err instanceof ApolloError &&
        err.graphQLErrors.some(
          (item) => item.extensions?.code === "TERMS_CHANGED",
        )
      ) {
        setChecked(false);
        await refetch().catch(() => undefined);
      }
      setSaveError(
        err instanceof Error
          ? err.message
          : "We could not save your acceptance. Please try again.",
      );
    }
  };

  if (error) {
    if (!showBanner) return null;
    return (
      <Box role="alert" css={{ p: "$5", backgroundColor: "$grey1" }}>
        <Text variant="b2">
          We could not check your terms status. Existing work is still
          available.
        </Text>
        <Button
          onClick={() => {
            void refetch().catch(() => undefined);
          }}
        >
          Retry terms check
        </Button>
      </Box>
    );
  }
  if (saved && showBanner)
    return (
      <Box role="status" css={{ p: "$5" }}>
        Terms accepted. You can continue with new work.
      </Box>
    );
  if (!terms?.acceptanceRequired) return null;

  return (
    <>
      {showBanner && (
        <Flex
          role="region"
          aria-label="Updated terms"
          css={{
            p: "$5",
            gap: "$4",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            backgroundColor: "$grey1",
          }}
        >
          <Text variant="b2">
            Please review Contact’s updated terms before you next submit or
            accept a job.
          </Text>
          <Button variant="primary" onClick={() => setOpen(true)}>
            Review updated terms
          </Button>
        </Flex>
      )}
      <Modal
        isOpen={open}
        onClose={close}
        title="Review Contact’s updated terms"
      >
        <Box
          css={{
            "& p": { mb: "$5" },
            "& a": { textDecoration: "underline" },
            overflowWrap: "break-word",
          }}
        >
          <Text variant="b2">
            Contact is operated by Cultural Utilities Ltd. These terms apply to
            new work from{" "}
            {new Date(terms.effectiveAt).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
              timeZone: "Europe/London",
            })}
            .
          </Text>
          <Text variant="b2">
            Review the sections on bookings, fees and payments, cancellations,
            content rights and liability, including the schedule for your role.
          </Text>
          <Text variant="b2">
            <a href={terms.termsUrl} target="_blank" rel="noreferrer">
              Read the Terms of Service
            </a>
            {" · "}
            <a href={terms.privacyUrl} target="_blank" rel="noreferrer">
              Read the Privacy Policy
            </a>
          </Text>
          <Text variant="b2">
            Existing confirmed bookings keep the terms agreed for those
            bookings. Accepting here does not change their fees, payment rights
            or other agreed terms, or settle any outstanding obligations.
          </Text>
          <Text variant="b2">
            If you are not ready to accept, you can still view existing work,
            message, manage payments and raise disputes. You only need to accept
            before you submit or accept a job.
          </Text>
          <Flex
            as="label"
            css={{
              gap: "$4",
              alignItems: "flex-start",
              mb: "$6",
              cursor: "pointer",
            }}
          >
            <Checkbox
              checked={checked}
              disabled={saving}
              onChange={(event) => setChecked(event.currentTarget.checked)}
              css={{ width: 24, height: 24, flexShrink: 0 }}
            />
            <span>
              I agree to the Terms of Service. If I am acting for a company or
              agency, I confirm I am authorised to agree on its behalf.
            </span>
          </Flex>
          <Text variant="meta">
            This does not change your marketing preferences. We record the terms
            version and the time you accept.
          </Text>
          {saveError && (
            <Text role="alert" variant="b2">
              {saveError}
            </Text>
          )}
          <Flex css={{ flexDirection: "column", gap: "$3" }}>
            <Button
              variant="primary"
              disabled={!checked || saving}
              onClick={submit}
            >
              {saving ? "Saving…" : "Accept terms"}
            </Button>
            <Button disabled={saving} onClick={close}>
              Not now — manage existing work
            </Button>
          </Flex>
          <Text variant="meta" css={{ mt: "$5" }}>
            To discuss the terms, email{" "}
            <a href="mailto:help@contact.xyz">help@contact.xyz</a>. You can
            delete your account yourself from your account settings; existing
            bookings and outstanding obligations still need to be addressed.
          </Text>
        </Box>
      </Modal>
    </>
  );
};
